import CopyButton from './shared/CopyButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import integrationsService from '../services/integrationsService';
import { apiErrorMessage } from '../utils/apiError';
import CustomCodeAssistant from './CustomCodeAssistant';
import CustomCodeTester from './shared/CustomCodeTester';
import ConfirmModal from './shared/ConfirmModal';
import Spinner from './shared/Spinner';
import './PartnerSyncTemplates.css';
import './IntegrationRouteCodePage.css';

// CUSTOM CODE ON ONE INTEGRATION ROUTE (V478) — opened from a route's Custom Code button on the designer.
//
// THE SAME EDITOR AS A SYNC TEMPLATE SNIPPET (Partner Settings > Templates > Custom Code): name, status, code,
// a browser-side test against a pasted payload, and Claude beside it. The pieces are the shared ones —
// CustomCodeTester and CustomCodeAssistant — so the two screens cannot drift apart on the execution
// contract or on how a suggestion is applied.
//
// WHAT IS DIFFERENT is who a snippet belongs to. Here it belongs to the INTEGRATION and names the routes it
// runs on, in sequence order. The screen opens on one route, lists what runs there, and lets a snippet be
// attached to other routes of the same integration without writing it twice.
//
// A FULL SCREEN, NOT A MODAL (STYLE.md): code is edited for longer than a dialog is meant to stay open.

// WHAT A SNIPPET RECEIVES AND RETURNS — the comment the example starts with, and the reference panel above the editor
// (shown for every snippet, not only a new one: Initialize fills an EMPTY editor, so saved code never showed it).
const CONTRACT = `// Custom code — the OHIP Adapter runs it on each record this route sends to the Universal API
// (core/route-custom-code), before the record is converted and sent.
//
//   payload : the record as the source sent it (OHIP-shaped JSON; the hub's meta taken off).
//   context : {
//     integrationId, routeId, implementationId, hotelId, module, transactionId,
//     message : { messageType, actionType }           — what this payload is
//     query   : { moduleName, actionType, actionId, primaryKey, reservationId, hotelId } — what was fetched
//     ohip    : the OHIP SDK, authenticated as the source — its token good for at least a minute
//               ohip.get(path, { query }) / post(path, body) / put / delete → parsed JSON ({hotelId} in a path = this hotel)
//               ohip.reservations.get(id) · ohip.reservations.folios(id) · ohip.profiles.get(id) · ohip.blocks.get(id)
//               ohip.businessDate() · ohip.refresh() — a 401 re-authenticates and retries by itself
//     auth    : ohip.auth = { gatewayUrl, hotelId, appKey, externalSystem, accessToken, expiresAt } — for your own calls
//     log(...)                                         — a line in the route's log step
//   }
//
// Return the payload (changed or not) to send it. Return an ARRAY to send several messages — it replaces the
// payload, so include it if it should still go:
//     return [payload, { messageType: 'FOLIO', actionType: 'CHECK OUT', reservationId: '123', record: folios }];
// A message's actionType defaults to the event's (e.g. UPDATE RESERVATION); set it when the message means something else.
// Each message gets meta, loses OHIP's links, and goes on this route (which must carry its module — /csh for FOLIO).
// Return [] to send nothing. May be async. A thrown exception fails the record on this route.
`;

// The example: at checkout, the reservation and its folios through the OHIP SDK (context.ohip).
const EXAMPLE = `async function transform(payload, context) {
    // Example: at checkout, send the reservation and its folios from OHIP cashiering (as legacy sqs_2323 did).
    var res = payload && payload.reservations && payload.reservations.reservation && payload.reservations.reservation[0];
    if (!res || res.reservationStatus !== 'CheckedOut' || !context.query.reservationId) return payload;
    try {
        // Every window, with postings, balances and transaction codes — the legacy request.
        var folios = await context.ohip.reservations.folios(context.query.reservationId);
        // CHECK OUT whatever event carried the checkout — the archive names it FOLIO_CHECK-OUT_<property>_<actionId>.json
        return [payload, { messageType: 'FOLIO', actionType: 'CHECK OUT', reservationId: context.query.reservationId, record: folios }];
    } catch (e) {
        context.log('folios not read: ' + e.message);   // the reservation still goes
        return payload;
    }
}
`;

const SKELETON = `${CONTRACT}${EXAMPLE}`;

const SUGGESTIONS = [
    'Explain what this snippet currently does',
    'Map the source room type codes to the backend codes',
    'Why might this throw on some records?',
];

const BackArrow = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const routeModules = (route) => (route && route.modules ? route.modules.map(m => m.uapi_route_path || m.name) : []);

const IntegrationRouteCodePage = () => {
    const { id, routeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // The editor's own query comes back with it — `from` and `mcpSource` decide where ITS Back goes.
    const backPath = `/pipeline-templates/${id}/edit${location.search || ''}`;

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [integration, setIntegration] = useState(null);
    const [graph, setGraph] = useState({ nodes: [], routes: [], customCode: [] });

    // The snippet on screen: { id?, name, code, active: 'Y'|'N', sequence, routeIds: [] } — null while the list shows.
    const [editing, setEditing] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [assistOpen, setAssistOpen] = useState(true);
    const [testPayload, setTestPayload] = useState('');
    const nameRef = useRef(null);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const [i, g] = await Promise.all([
                integrationsService.getIntegrationById(id),
                integrationsService.getGraph(id),
            ]);
            setIntegration(i.integration || i);
            setGraph({ nodes: g.nodes || [], routes: g.routes || [], customCode: g.customCode || [] });
        } catch (e) {
            setLoadError(apiErrorMessage(e, 'The integration could not be loaded.'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const route = useMemo(
        () => graph.routes.find(r => String(r.id) === String(routeId)) || null, [graph.routes, routeId]);
    const nodeOf = (nodeId) => graph.nodes.find(n => String(n.id) === String(nodeId)) || null;
    const source = route ? nodeOf(route.from_node_id) : null;

    // How a route is named in lists: what it carries, and where it lands.
    const routeLabel = (r) => `${routeModules(r).join(' ') || 'no modules'} → ${r.backend_systems || 'no backend'}`;

    const onThisRoute = graph.customCode.filter(c => (c.route_ids || []).some(r => String(r) === String(routeId)));
    const elsewhere = graph.customCode.filter(c => !onThisRoute.includes(c));

    const openNew = () => {
        setSaveError(null);
        setNotice(null);
        setEditing({ name: '', code: '', active: 'N', sequence: onThisRoute.length * 10, routeIds: [Number(routeId)] });
    };

    const openExisting = async (snippetId) => {
        setSaveError(null);
        setNotice(null);
        setEditLoading(true);
        try {
            const { snippet } = await integrationsService.getCustomCode(id, snippetId);
            setEditing({
                id: snippet.id,
                name: snippet.name || '',
                code: snippet.code || '',
                active: snippet.active === 'Y' ? 'Y' : 'N',
                sequence: snippet.sequence || 0,
                routeIds: (snippet.route_ids || []).map(Number),
            });
        } catch (e) {
            setNotice({ kind: 'error', text: apiErrorMessage(e, 'That snippet could not be opened.') });
        } finally {
            setEditLoading(false);
        }
    };

    // Attach an existing snippet of this integration to this route, without opening it.
    const attach = async (snippet) => {
        try {
            await integrationsService.updateCustomCode(id, snippet.id, {
                routeIds: [...new Set([...(snippet.route_ids || []), Number(routeId)])],
            });
            setNotice({ kind: 'ok', text: `"${snippet.name}" now runs on this route.` });
            await load();
        } catch (e) {
            setNotice({ kind: 'error', text: apiErrorMessage(e, 'The snippet could not be attached.') });
        }
    };

    const save = async () => {
        if (!editing) return;
        // A disabled Save explains nothing — accept the click and name what is missing.
        if (!editing.name.trim()) {
            setSaveError('Give the snippet a name before saving.');
            if (nameRef.current) nameRef.current.focus();
            return;
        }
        setSaving(true);
        setSaveError(null);
        const fields = {
            name: editing.name.trim(),
            code: editing.code,
            sequence: Number(editing.sequence) || 0,
            routeIds: editing.routeIds,
        };
        try {
            let snippetId = editing.id;
            if (snippetId) {
                await integrationsService.updateCustomCode(id, snippetId, { ...fields, active: editing.active });
            } else {
                // THE SERVER CREATES IT INACTIVE whatever is sent — code is written before it is right. If the
                // author switched it on before the first save, that is applied as its own update.
                const created = await integrationsService.createCustomCode(id, fields);
                snippetId = created.id;
                if (editing.active === 'Y') {
                    await integrationsService.updateCustomCode(id, snippetId, { active: 'Y' });
                }
            }
            setEditing(e => ({ ...e, id: snippetId }));
            setNotice({ kind: 'ok', text: `Saved "${fields.name}".` });
            await load();
        } catch (e) {
            setSaveError(apiErrorMessage(e, 'The snippet could not be saved.'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setConfirmDelete(false);
        if (!editing || !editing.id) return;
        try {
            await integrationsService.deleteCustomCode(id, editing.id);
            setNotice({ kind: 'ok', text: `Deleted "${editing.name}".` });
            setEditing(null);
            await load();
        } catch (e) {
            setSaveError(apiErrorMessage(e, 'The snippet could not be deleted.'));
        }
    };

    const toggleRoute = (rid) => setEditing(e => {
        const has = e.routeIds.some(x => String(x) === String(rid));
        return { ...e, routeIds: has ? e.routeIds.filter(x => String(x) !== String(rid)) : [...e.routeIds, Number(rid)] };
    });

    if (loading) return <div className="pst-container"><Spinner /></div>;

    const header = (
        <div className="pst-detail-header pst-detail-header-stacked">
            <button
                className="pst-back-btn"
                onClick={() => (editing ? setEditing(null) : navigate(backPath))}
                disabled={saving}
            >
                <BackArrow /> {editing ? 'Back to route code' : 'Back to integration'}
            </button>
            <div className="pst-detail-header-main">
                <div>
                    <div className="pst-detail-title">
                        {editing ? (editing.id ? 'Edit Code' : 'Add Code') : 'Route Custom Code'}
                    </div>
                    {route && (
                        <div className="irc-route">
                            {integration && integration.name && <span className="irc-integration">{integration.name}</span>}
                            <span className="irc-hop">{source ? (source.adapter || source.system_name) : 'source'}</span>
                            <span className="irc-arrow" aria-hidden="true">→</span>
                            <span className="irc-mods">
                                {routeModules(route).map(m => <code key={m}>{m}</code>)}
                            </span>
                            <span className="irc-arrow" aria-hidden="true">→</span>
                            <span className="irc-hop">{route.backend_systems || 'no backend'}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (loadError || !route) {
        return (
            <div className="pst-container">
                {header}
                <div className="irc-empty">
                    {loadError || (
                        <>
                            This route is no longer part of the design. Routes are renumbered each time the design
                            is saved, so a link to one can go stale — open Custom Code from the route on the
                            integration again. Snippets attached to it were carried over to its new number.
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="pst-container">
            {header}

            {notice && (
                <div className={`irc-notice ${notice.kind === 'error' ? 'is-error' : ''}`} role="status">{notice.text}</div>
            )}

            {!editing && (
                <div className="irc-list">
                    <div className="irc-list-head">
                        <div>
                            <h3>Runs on this route</h3>
                            <p>In sequence order. Each snippet receives the previous one's output.</p>
                        </div>
                        <button type="button" className="btn btn-primary" onClick={openNew}>+ Add Code</button>
                    </div>

                    {onThisRoute.length === 0 ? (
                        <div className="irc-empty">No custom code runs on this route yet.</div>
                    ) : (
                        <ul className="irc-rows">
                            {onThisRoute.map(c => (
                                <li key={c.id}>
                                    <button type="button" className="irc-row" onClick={() => openExisting(c.id)} disabled={editLoading}>
                                        <span className="irc-seq">{c.sequence}</span>
                                        <span className="irc-name">{c.name}</span>
                                        <span className={`irc-state ${c.active === 'Y' ? 'is-on' : ''}`}>{c.active === 'Y' ? 'Active' : 'Inactive'}</span>
                                        {(c.route_ids || []).length > 1 && (
                                            <span className="irc-shared">also on {(c.route_ids || []).length - 1} other route{(c.route_ids || []).length > 2 ? 's' : ''}</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {elsewhere.length > 0 && (
                        <div className="irc-attach">
                            <h3>Other snippets on this integration</h3>
                            <ul className="irc-rows">
                                {elsewhere.map(c => (
                                    <li key={c.id} className="irc-row irc-row-static">
                                        <span className="irc-name">{c.name}</span>
                                        <span className={`irc-state ${c.active === 'Y' ? 'is-on' : ''}`}>{c.active === 'Y' ? 'Active' : 'Inactive'}</span>
                                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => attach(c)}>Run on this route</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {editing && (
                <div className="pst-code-screen">
                    <div className="pst-form-row">
                        <div className="pst-form-group">
                            <label htmlFor="irc-name">Name *</label>
                            <input
                                id="irc-name"
                                ref={nameRef}
                                value={editing.name}
                                onChange={e => { setSaveError(null); setEditing({ ...editing, name: e.target.value }); }}
                                placeholder="e.g. Default market code"
                                autoFocus
                            />
                        </div>
                        <div className="pst-form-group irc-seq-group">
                            <label htmlFor="irc-sequence">Sequence</label>
                            <input
                                id="irc-sequence"
                                type="number"
                                value={editing.sequence}
                                onChange={e => setEditing({ ...editing, sequence: e.target.value })}
                            />
                        </div>
                        <div className="pst-form-group">
                            <label>Status</label>
                            <button
                                type="button"
                                className={`pst-active-toggle ${editing.active === 'Y' ? 'is-on' : 'is-off'}`}
                                aria-pressed={editing.active === 'Y'}
                                onClick={() => setEditing({ ...editing, active: editing.active === 'Y' ? 'N' : 'Y' })}
                            >
                                <span className="pst-active-dot" />
                                {editing.active === 'Y' ? 'Active — runs on its routes' : 'Inactive — not run'}
                            </button>
                        </div>
                    </div>

                    {/* THE ROUTES IT RUNS ON. One snippet can serve several routes of this integration, so it
                        is written once rather than copied per route and left to drift. */}
                    <fieldset className="pst-form-group irc-routes">
                        <legend>Runs on</legend>
                        {/* THE ROUTE THIS SCREEN WAS OPENED FROM COMES FIRST, and a new snippet arrives
                            with it already ticked — it is the one the Custom Code button was pressed on,
                            so it is the answer until somebody says otherwise. Buried among the
                            integration's other routes it read as a list of equals and the tick was easy
                            to miss. */}
                        {[...graph.routes]
                            .sort((a, b) => (String(b.id) === String(routeId) ? 1 : 0)
                                - (String(a.id) === String(routeId) ? 1 : 0))
                            .map(r => (
                            <label key={r.id} className="irc-route-check">
                                <input
                                    type="checkbox"
                                    checked={editing.routeIds.some(x => String(x) === String(r.id))}
                                    onChange={() => toggleRoute(r.id)}
                                />
                                <span>{routeLabel(r)}</span>
                                {String(r.id) === String(routeId) && <span className="irc-this">this route</span>}
                                {r.active !== 'Y' && <span className="irc-state">route inactive</span>}
                            </label>
                            ))}
                    </fieldset>

                    <div className="pst-form-group">
                        <div className="irc-code-head">
                            <label htmlFor="irc-code">Code</label>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setEditing({ ...editing, code: SKELETON })}
                                disabled={!!(editing.code && editing.code.trim())}
                                title={editing.code && editing.code.trim() ? 'Clear the editor first to start from the example' : 'Start from an example'}
                            >
                                Initialize
                            </button>
                        </div>
                        {/* THE CONTRACT, ALWAYS TO HAND — what context carries (the OHIP SDK, query, auth) and what may be
                            returned; with the folio example to copy into code that already exists. */}
                        <details className="irc-contract" style={{ margin: '0.25rem 0 0.5rem' }}>
                            <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#4b5563' }}>
                                What your code receives — the OHIP SDK, query, auth — and can return
                            </summary>
                            <pre style={{ fontSize: '0.78rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6,
                                          padding: '0.6rem 0.75rem', whiteSpace: 'pre-wrap', margin: '0.4rem 0' }}>
                                {CONTRACT.replace(/^\/\/ ?/gm, '')}
                            </pre>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#4b5563' }}>
                                Example — checkout folios through the SDK <CopyButton value={EXAMPLE} />
                            </div>
                            <pre style={{ fontSize: '0.78rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6,
                                          padding: '0.6rem 0.75rem', whiteSpace: 'pre-wrap', margin: '0.4rem 0 0' }}>
                                {EXAMPLE}
                            </pre>
                        </details>
                        <textarea
                            id="irc-code"
                            className="pst-code-editor"
                            value={editing.code}
                            onChange={e => setEditing({ ...editing, code: e.target.value })}
                            spellCheck={false}
                            rows={20}
                            placeholder="// custom code snippet — click Initialize for an example"
                        />
                    </div>

                    <div className="pst-test">
                        <button type="button" className="pst-test-toggle" onClick={() => setTestOpen(!testOpen)}>
                            <span className={`pst-test-chevron ${testOpen ? 'open' : ''}`}>&#9656;</span>
                            Test against a payload
                        </button>
                        {testOpen && (
                            <CustomCodeTester
                                code={editing.code}
                                context={{
                                    integrationId: Number(id),
                                    routeId: Number(routeId),
                                    hotelId: integration && integration.hotel_id ? Number(integration.hotel_id) : null,
                                    module: routeModules(route)[0] || null,
                                    method: 'POST',
                                }}
                                payload={testPayload}
                                onPayloadChange={setTestPayload}
                                placeholder='Paste the Universal API document this route sends, e.g. { "reservations": { "reservation": [ ... ] } }'
                                failureMeaning="on the route this would fail the record."
                            />
                        )}
                    </div>

                    <div className="pst-test">
                        <button type="button" className="pst-test-toggle" onClick={() => setAssistOpen(!assistOpen)}>
                            <span className={`pst-test-chevron ${assistOpen ? 'open' : ''}`}>&#9656;</span>
                            Develop with Claude
                        </button>
                        {assistOpen && (
                            <div className="pst-test-body">
                                <CustomCodeAssistant
                                    assist={(messages, ctx) => integrationsService.assistCustomCode(id, messages, ctx)}
                                    suggestions={SUGGESTIONS}
                                    note="knows the route, its modules, the backend and your current code"
                                    snippetContext={{
                                        code: editing.code || '',
                                        integrationName: integration ? integration.name : null,
                                        sourceSystem: source ? source.system_name : null,
                                        proxy: source ? source.adapter : null,
                                        modules: routeModules(route),
                                        backendSystem: route.backend_systems || null,
                                        samplePayload: testPayload || '',
                                    }}
                                    onInsert={(code) => setEditing(e => ({ ...e, code }))}
                                />
                            </div>
                        )}
                    </div>

                    <div className="pst-modal-actions">
                        {saveError && <div className="pst-save-error" role="alert">{saveError}</div>}
                        {editing.id && (
                            <button type="button" className="btn btn-secondary irc-delete" onClick={() => setConfirmDelete(true)} disabled={saving}>
                                Delete
                            </button>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <ConfirmModal
                    title="Delete this snippet?"
                    message={`"${editing.name}" will stop running on every route it is attached to${(editing.routeIds || []).length > 1 ? ` (${editing.routeIds.length} routes)` : ''}. This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={remove}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    );
};

export default IntegrationRouteCodePage;
